import axios from 'axios';
import { API_URL } from '../apis/apiurl';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Global loader state management
let loaderCount = 0;
let loaderCallbacks = {
  showLoader: null,
  hideLoader: null,
};

// Cache to track which pages have been loaded
const pageLoadCache = new Set();
let currentPage = '';

// Function to check if we're on auth pages
const isAuthPage = () => {
  const authPages = ['/', '/login', '/register', '/resetpassword', '/role'];
  const currentPath = window.location.pathname;
  return authPages.includes(currentPath) || currentPath.includes('/verifyuser');
};

// Function to check if this is a page navigation (not tab switch)
const isPageNavigation = () => {
  const newPage = window.location.pathname;
  const isNavigation = newPage !== currentPage;
  currentPage = newPage;
  return isNavigation;
};

// Function to check if page data is cached
const isPageDataCached = (page) => {
  return pageLoadCache.has(page);
};

// Function to mark page as loaded
const markPageAsLoaded = (page) => {
  pageLoadCache.add(page);
};

// Function to clear page cache (useful for logout or force refresh)
export const clearPageCache = () => {
  pageLoadCache.clear();
  currentPage = '';
};

// Function to set loader callbacks (called from app context)
export const setLoaderCallbacks = (callbacks) => {
  loaderCallbacks = callbacks;
};

// Function to show loader
const showLoader = (text = 'Loading...') => {
  if (loaderCallbacks.showLoader) {
    loaderCallbacks.showLoader(text);
  }
};

// Function to hide loader
const hideLoader = () => {
  if (loaderCallbacks.hideLoader) {
    loaderCallbacks.hideLoader();
  }
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Increment loader count
    loaderCount++;
    
    // Only show loader if:
    // 1. This is the first request
    // 2. We're not on auth pages
    // 3. This is a page navigation (not tab switch) OR page data is not cached
    const shouldShowLoader = loaderCount === 1 && 
                           !isAuthPage() && 
                           (isPageNavigation() || !isPageDataCached(window.location.pathname));
    
    if (shouldShowLoader) {
      const loadingText = config.loadingText || 'Loading...';
      showLoader(loadingText);
    }
    
    return config;
  },
  (error) => {
    // Decrement loader count on error
    loaderCount = Math.max(0, loaderCount - 1);
    
    // Hide loader if no more requests and not on auth pages
    if (loaderCount === 0 && !isAuthPage()) {
      hideLoader();
    }
    
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Decrement loader count
    loaderCount = Math.max(0, loaderCount - 1);
    
    // Mark page as loaded when all requests are complete
    if (loaderCount === 0 && !isAuthPage()) {
      markPageAsLoaded(window.location.pathname);
      hideLoader();
    }
    
    return response;
  },
  (error) => {
    // Decrement loader count on error
    loaderCount = Math.max(0, loaderCount - 1);
    
    // Hide loader if no more requests and not on auth pages
    if (loaderCount === 0 && !isAuthPage()) {
      hideLoader();
    }
    
    return Promise.reject(error);
  }
);

// GraphQL helper function
export const graphqlRequest = async (query, variables = {}, loadingText = '') => {
  try {
    const response = await apiClient.post('/graphql', {
      query,
      variables,
    }, {
      loadingText, // Custom loading text for this request
    });
    
    if (response.data.errors) {
      throw new Error(response.data.errors[0]?.message || 'GraphQL Error');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('GraphQL Request Error:', error);
    throw error;
  }
};

// REST API helper function
export const restRequest = async (method, endpoint, data = null, loadingText = '') => {
  try {
    const config = {
      method,
      url: endpoint,
      loadingText,
    };
    
    if (data) {
      config.data = data;
    }
    
    const response = await apiClient(config);
    return response.data;
  } catch (error) {
    console.error('REST Request Error:', error);
    throw error;
  }
};

// Export the configured axios instance
export default apiClient;

// Fetch wrapper with loader support
export const fetchWithLoader = async (url, options = {}, loadingText = '') => {
  try {
    // Show loader
    loaderCount++;
    
    // Only show loader if this is a page navigation or page data is not cached
    const shouldShowLoader = loaderCount === 1 && 
                           !isAuthPage() && 
                           (isPageNavigation() || !isPageDataCached(window.location.pathname));
    
    if (shouldShowLoader) {
      showLoader(loadingText);
    }

    const response = await fetch(url, options);
    
    // Hide loader
    loaderCount = Math.max(0, loaderCount - 1);
    if (loaderCount === 0 && !isAuthPage()) {
      markPageAsLoaded(window.location.pathname);
      hideLoader();
    }

    return response;
  } catch (error) {
    // Hide loader on error
    loaderCount = Math.max(0, loaderCount - 1);
    if (loaderCount === 0 && !isAuthPage()) {
      hideLoader();
    }
    throw error;
  }
};

// Export individual methods for convenience
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
  graphql: graphqlRequest,
  rest: restRequest,
  fetch: fetchWithLoader,
};
